import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme, Theme } from '../../contexts/ThemeContext';

interface ThemeToggleProps {
  onThemeChange?: () => void;
}

// Session storage key for themes cache
const THEMES_CACHE_KEY = 'available_themes';

// In-memory cache and promise for the current session
let themesCache: string[] | null = null;
let themesCachePromise: Promise<string[]> | null = null;

const fetchAvailableThemes = async (): Promise<string[]> => {
  // Return memory cache if available
  if (themesCache !== null) {
    return themesCache;
  }

  // Check session storage first
  try {
    const cached = sessionStorage.getItem(THEMES_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed)) {
        themesCache = parsed;
        return themesCache;
      }
    }
  } catch (error) {
    
  }

  // Return existing promise if already fetching
  if (themesCachePromise !== null) {
    return themesCachePromise;
  }

  // Create new promise for fetching themes
  themesCachePromise = import('../../api').then(({ api }) => {
    return api.get('/themes/')
      .then(response => {
        
        themesCache = response.data;
        
        // Cache in session storage
        try {
          sessionStorage.setItem(THEMES_CACHE_KEY, JSON.stringify(themesCache));
        } catch (error) {
          
        }
        
        themesCachePromise = null; // Reset promise after completion
        return themesCache as string[];
      })
      .catch(error => {
        
        themesCachePromise = null; // Reset promise on error
        const emptyArray: string[] = [];
        return emptyArray;
      });
  });

  return themesCachePromise;
};

export default function ThemeToggle({ onThemeChange }: ThemeToggleProps) {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const [availableThemes, setAvailableThemes] = useState<string[]>([]);

  useEffect(() => {
    fetchAvailableThemes().then(themes => {
      setAvailableThemes(themes);
    });
  }, []);

  const handleThemeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newTheme = e.target.value as Theme;
    setTheme(newTheme);
    // Call the callback to close the menu
    onThemeChange?.();
  };

  const getThemeLabel = (themeName: string) => {
    // Always use the filename as the display name (capitalized)
    return themeName.charAt(0).toUpperCase() + themeName.slice(1);
  };

  return (
    <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
      <span className="text-sm text-gray-600 dark:text-gray-300">{t('theme.theme')}</span>
      <select
        value={theme}
        onChange={handleThemeChange}
        onClick={(e) => e.stopPropagation()}
        className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="light">{t('theme.light')}</option>
        <option value="dark">{t('theme.dark')}</option>
        {availableThemes.map(themeName => (
          <option key={themeName} value={themeName}>
            {getThemeLabel(themeName)}
          </option>
        ))}
        <option value="auto">{t('theme.auto')}</option>
      </select>
    </div>
  );
}